import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, Info, Package, ShieldAlert, Lock, AlertTriangle, History, Handshake, Truck } from 'lucide-react';
import { itemsAPI } from '../lib/api';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null);
const shortId = (id) => (id ? id.slice(-6).toUpperCase() : '');
const humanize = (s) => (s ? s.replace(/_/g, ' ') : '');

export function AvailabilityBreakdownModal({ open, onOpenChange, data, loading }) {
  if (!data && !loading) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="availability-breakdown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Info className="w-5 h-5 text-primary" />
            Why isn't this available?
          </DialogTitle>
          <DialogDescription className="sr-only">Availability breakdown</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <BreakdownBody data={data} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function BreakdownBody({ data }) {
  const t = data.totals;
  const holding = data.allocations.length + data.reservations.length + data.quarantines.length;
  return (
    <div className="space-y-4 min-w-0">
      <ItemHeader data={data} />
      <SummaryGrid totals={t} tracked={data.tracked} />
      {!data.tracked.allocated && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="breakdown-vehicle-note">
          <Truck className="w-3.5 h-3.5 shrink-0" /> Job allocations and part-request reservations aren't tracked for vehicle stock.
        </p>
      )}
      <Checks checks={data.checks} totals={t} />
      {data.rows.length > 1 && <RowsTable rows={data.rows} />}
      {holding === 0 && data.checks.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-3 rounded-lg border border-dashed" data-testid="breakdown-empty">
          Nothing is holding this stock: all {t.on_hand} on hand {t.on_hand === 1 ? 'is' : 'are'} available.
        </p>
      ) : (
        <>
          {data.tracked.allocated && <AllocationSection allocations={data.allocations} zeroCount={data.zero_qty_allocations || 0} />}
          {data.tracked.reserved && <ReservationSection reservations={data.reservations} shared={data.reservations_shared} />}
          <QuarantineSection quarantines={data.quarantines} />
        </>
      )}
      <ActivitySection activity={data.activity} />
    </div>
  );
}

function ItemHeader({ data }) {
  const row = data.rows.length === 1 ? data.rows[0] : null;
  return (
    <div className="min-w-0" data-testid="breakdown-item-header">
      <p className="font-semibold truncate">{data.item.name}</p>
      <p className="text-xs text-muted-foreground truncate">
        {[data.item.sku && `SKU ${data.item.sku}`, data.item.manufacturer_part_number && `MPN ${data.item.manufacturer_part_number}`, data.location_name].filter(Boolean).join(' · ')}
      </p>
      {row?.ownership_type === 'consigned' && (
        <Badge variant="outline" className="mt-1 text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
          <Handshake className="w-3 h-3 mr-1" /> Consigned{row.vendor_name ? ` from ${row.vendor_name}` : ''}
        </Badge>
      )}
    </div>
  );
}

function SummaryGrid({ totals, tracked }) {
  const cards = [
    { key: 'on_hand', label: 'Total On Hand' },
    { key: 'available', label: 'Available', highlight: true },
    ...(tracked.allocated ? [{ key: 'allocated', label: 'Allocated to Jobs', warn: true }] : []),
    ...(tracked.reserved ? [{ key: 'reserved', label: 'Reserved for Part Requests', warn: true }] : []),
    { key: 'quarantined', label: 'Quarantined', warn: true },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="breakdown-summary">
      {cards.map(c => <SumCard key={c.key} testId={`breakdown-sum-${c.key}`} {...c} value={totals[c.key]} />)}
    </div>
  );
}

function SumCard({ label, value, warn, highlight, testId }) {
  const active = warn && value > 0;
  return (
    <div className={`px-2 py-1.5 rounded-lg border min-w-0 ${
      highlight ? 'bg-primary/10 border-primary/30' : active ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800' : 'bg-muted/50'
    }`} data-testid={testId}>
      <p className="text-[11px] leading-tight text-muted-foreground">{label}</p>
      <p className={`font-bold text-base font-mono tabular-nums ${highlight ? 'text-primary' : active ? 'text-amber-700 dark:text-amber-300' : ''}`}>{value}</p>
    </div>
  );
}

const CHECK_TEXT = {
  allocated_mismatch: (c) => `The allocated total on this stock record is ${c.counter}, but active allocation records add up to ${c.records}.`,
  reserved_mismatch: (c) => `The reserved total is ${c.counter}, but approved or staged part requests add up to ${c.records}.`,
  quarantined_mismatch: (c) => `The quarantined total is ${c.counter}, but active quarantine records add up to ${c.records}.`,
  negative_on_hand: (c) => `On hand is negative (${c.on_hand}).`,
};

function Checks({ checks, totals }) {
  if (!checks.length) return null;
  const over = checks.find(c => c.code === 'over_allocated');
  const short = checks.find(c => c.code === 'reserved_short');
  const others = checks.filter(c => c.code !== 'over_allocated' && c.code !== 'reserved_short');
  return (
    <div className="space-y-2">
      {over && (
        <div className="flex gap-2 p-3 rounded-lg border border-red-300 bg-red-50 text-red-800 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300" data-testid="breakdown-over-allocated">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="text-sm">
            <span className="font-semibold">Over by {over.over_by}.</span> {totals.allocated + totals.quarantined} are allocated or quarantined
            but only {totals.on_hand} on hand. Available shows 0. These records need manual reconciliation.
          </p>
        </div>
      )}
      {short && (
        <div className="flex gap-2 p-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300" data-testid="breakdown-reserved-short">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="text-xs">Part requests reserve {short.short_by} more than is left after allocations and quarantine. They are waiting for stock.</p>
        </div>
      )}
      {others.map(c => (
        <div key={c.code} className="flex gap-2 p-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300" data-testid={`breakdown-check-${c.code}`}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="text-xs">{CHECK_TEXT[c.code]?.(c)} Needs manual review.</p>
        </div>
      ))}
    </div>
  );
}

function RowsTable({ rows }) {
  return (
    <Section title="Stock records" testId="breakdown-rows">
      {rows.map(r => (
        <div key={r.inventory_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs p-2 rounded-md border bg-card">
          <Badge variant="outline" className="capitalize text-[11px]">{r.ownership_type}{r.vendor_name ? ` · ${r.vendor_name}` : ''}</Badge>
          <span>On hand <b>{r.on_hand}</b></span><span>Available <b>{r.available}</b></span>
          <span>Allocated {r.allocated}</span><span>Reserved {r.reserved}</span><span>Quarantined {r.quarantined}</span>
          {r.over_allocated_by > 0 && <span className="text-red-600 dark:text-red-400 font-semibold">Over by {r.over_allocated_by}</span>}
          {r.reserved_short_by > 0 && <span className="text-amber-700 dark:text-amber-300">Short {r.reserved_short_by} for requests</span>}
        </div>
      ))}
    </Section>
  );
}

function Section({ title, icon: Icon, count, testId, children }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}{title}{count !== undefined && <span className="font-mono">({count})</span>}
      </p>
      {children}
    </div>
  );
}

function AllocationSection({ allocations, zeroCount }) {
  const groups = Object.values(allocations.reduce((acc, a) => {
    const k = a.job_id || a.job_number;
    (acc[k] = acc[k] || { key: k, job_number: a.job_number, job_name: a.job_name, total: 0, items: [] }).items.push(a);
    acc[k].total += a.quantity;
    return acc;
  }, {}));
  return (
    <Section title="Allocated to jobs" icon={Package} count={groups.length} testId="breakdown-allocations">
      {zeroCount > 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="breakdown-zero-allocations-note">
          {zeroCount} other allocation {zeroCount === 1 ? 'record holds' : 'records hold'} 0 units and {zeroCount === 1 ? "isn't" : "aren't"} listed.
        </p>
      )}
      {allocations.some(a => !a.linked) && (
        <p className="text-[11px] text-muted-foreground" data-testid="breakdown-unverified-note">
          Allocations marked "Stock record unverified" are older records that aren't linked to a specific stock record. They're counted in this warehouse's allocated total, but whether they came from owned or consigned stock can't be confirmed.
        </p>
      )}
      {groups.length === 0 ? <Empty text="No active job allocations." /> : groups.map(g => (
        <div key={g.key} className="rounded-lg border bg-card p-2.5 space-y-1" data-testid={`breakdown-job-${g.job_number}`}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium min-w-0 truncate">#{g.job_number}{g.job_name ? ` — ${g.job_name}` : ''}</p>
            <span className="font-mono font-bold text-sm tabular-nums shrink-0">{g.total}</span>
          </div>
          {g.items.map(a => <AllocationLine key={a.id} a={a} showQty={g.items.length > 1} />)}
        </div>
      ))}
    </Section>
  );
}

function AllocationLine({ a, showQty }) {
  const detail = [
    `Allocation ${shortId(a.id)}`,
    a.part_request_id ? `from part request ${shortId(a.part_request_id)}` : 'direct',
    humanize(a.status),
    (a.qty_used || a.qty_returned) ? `${a.qty_allocated} allocated, ${a.qty_used} used, ${a.qty_returned} returned` : null,
    fmtDate(a.created_at), a.user_name,
  ].filter(Boolean).join(' · ');
  return (
    <div className="flex items-start justify-between gap-2 text-xs text-muted-foreground" data-testid={`breakdown-allocation-${a.id}`}>
      <p className="min-w-0 break-words">{!a.linked && <UnverifiedBadge testId={`unverified-allocation-${a.id}`} />}{detail}{a.notes ? ` — ${a.notes}` : ''}</p>
      {showQty && <span className="font-mono tabular-nums shrink-0">{a.quantity}</span>}
    </div>
  );
}

const UNVERIFIED_HELP = "Older record that isn't linked to a specific stock record. It's counted in this warehouse's allocated total, but whether it came from owned or consigned stock can't be confirmed.";

function UnverifiedBadge({ testId }) {
  return (
    <span title={UNVERIFIED_HELP} data-testid={testId}
      className="inline-flex items-center mr-1.5 rounded border border-amber-300 bg-amber-50 px-1 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 whitespace-nowrap">
      Stock record unverified
    </span>
  );
}

function ReservationSection({ reservations, shared }) {
  return (
    <Section title="Reserved for part requests" icon={Lock} count={reservations.length} testId="breakdown-reservations">
      {shared && reservations.length > 0 && <p className="text-[11px] text-muted-foreground" data-testid="breakdown-reservations-shared-note">These requests reserve this item at this warehouse as a whole. They aren't tied to owned or consigned stock, and each is counted once.</p>}
      {reservations.length === 0 ? <Empty text="No approved or staged part requests." /> : reservations.map(r => (
        <Row key={r.line_item_id} testId={`breakdown-reservation-${r.part_request_id}`} qty={r.quantity}
          title={r.job_number ? `#${r.job_number}${r.job_name ? ` — ${r.job_name}` : ''}` : 'No job'}
          detail={[`Part request ${shortId(r.part_request_id)}`, r.status, r.staged ? `${r.staged} staged` : null,
            r.approved_at ? `approved ${fmtDate(r.approved_at)}` : `requested ${fmtDate(r.requested_at)}`, r.requested_by].filter(Boolean).join(' · ')} />
      ))}
    </Section>
  );
}

function QuarantineSection({ quarantines }) {
  return (
    <Section title="Quarantined" icon={ShieldAlert} count={quarantines.length} testId="breakdown-quarantines">
      {quarantines.length === 0 ? <Empty text="No active quarantine records." /> : quarantines.map(q => (
        <Row key={q.id} testId={`breakdown-quarantine-${q.id}`} qty={q.quantity} title={`${humanize(q.reason)} · ${q.status}`}
          badge={!q.linked && <UnverifiedBadge testId={`unverified-quarantine-${q.id}`} />}
          detail={[`Quarantine ${shortId(q.id)}`, fmtDate(q.quarantined_at), q.user_name, q.hold_until ? `hold until ${fmtDate(q.hold_until)}` : null,
            q.notes].filter(Boolean).join(' · ')} />
      ))}
    </Section>
  );
}

function ActivitySection({ activity }) {
  if (!activity.length) return null;
  return (
    <Section title="Recent activity" icon={History} testId="breakdown-activity">
      <div className="rounded-lg border divide-y">
        {activity.map(a => (
          <div key={a.id} className="flex items-start justify-between gap-2 px-2.5 py-1.5 text-xs">
            <p className="min-w-0 break-words"><span className="font-medium capitalize">{humanize(a.action_type)}</span>
              <span className="text-muted-foreground"> · {[fmtDate(a.created_at), a.user_name, a.notes].filter(Boolean).join(' · ')}</span></p>
            <span className="font-mono tabular-nums shrink-0">{a.quantity}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Row({ title, detail, qty, testId, badge }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-lg border bg-card p-2.5" data-testid={testId}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-xs text-muted-foreground break-words">{badge}{detail}</p>
      </div>
      <span className="font-mono font-bold text-sm tabular-nums shrink-0">{qty}</span>
    </div>
  );
}

const Empty = ({ text }) => <p className="text-xs text-muted-foreground">{text}</p>;

export function useAvailabilityBreakdown() {
  const [state, setState] = useState({ open: false, data: null, loading: false });

  const openBreakdown = async (itemId, locationType, locationId, inventoryId) => {
    setState({ open: true, data: null, loading: true });
    try {
      const res = await itemsAPI.getAvailabilityBreakdown(itemId, locationType, locationId, inventoryId);
      setState({ open: true, data: res.data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the availability breakdown');
      setState({ open: false, data: null, loading: false });
    }
  };

  const setOpen = (open) => setState(prev => ({ ...prev, open }));
  return [state, openBreakdown, setOpen];
}
